import { useEffect, useState } from "react";
import { useShowContext } from "ra-core";
import { useSearchParams } from "react-router";
import type { Contact } from "@/components/atomic-crm/types";
import type { CompanyWithPrimaryContact } from "@/modules/clients/clientProfile";
import { ClientAddContactDialog } from "@/modules/clients/ClientAddContactDialog";
import { ClientEditDialog } from "@/modules/clients/ClientEditDialog";
import { ClientShowActions } from "@/modules/clients/ClientShowActions";
import { ContactShowSheet } from "@/modules/clients/ContactShowSheet";
import { CompanyRecordView } from "@/modules/clients/record/RecordViews";

/**
 * Account record. Full page: fields and people on the left, tabs on the
 * right. Embedded (side panel): Home | Timeline | Tasks | More.
 */
export const ClientShowContent = ({
  embedded = false,
  onClose,
}: {
  embedded?: boolean;
  onClose?: () => void;
} = {}) => {
  const { record, isPending } = useShowContext<CompanyWithPrimaryContact>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [openContact, setOpenContact] = useState<Contact | null>(null);

  useEffect(() => {
    if (embedded) return;
    if (searchParams.get("edit") === "1") setEditOpen(true);
  }, [embedded, searchParams]);

  const handleEditOpenChange = (open: boolean) => {
    setEditOpen(open);
    if (!open && searchParams.get("edit") === "1") {
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
  };

  if (isPending || !record) return null;

  return (
    <div className={embedded ? "h-full min-h-0" : "lg:h-full lg:min-h-0"}>
      {embedded ? null : (
        <ClientShowActions record={record} onEdit={() => setEditOpen(true)} />
      )}

      <CompanyRecordView
        record={record}
        mode={embedded ? "panel" : "page"}
        onClose={onClose}
        onAddContact={() => setAddContactOpen(true)}
        onOpenContact={setOpenContact}
      />

      <ClientEditDialog
        companyId={record.id}
        open={editOpen}
        onOpenChange={handleEditOpenChange}
      />
      <ClientAddContactDialog
        companyId={record.id}
        open={addContactOpen}
        onOpenChange={setAddContactOpen}
      />
      <ContactShowSheet
        contactId={openContact?.id ?? null}
        open={openContact != null}
        onOpenChange={(open) => {
          if (!open) setOpenContact(null);
        }}
      />
    </div>
  );
};
